import { useState } from "react";
import {
  IconButton,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import t from "../translations";
import { type DatasetImageFilters } from "./useDatasetImageFilter";

// Each split-filter toggle option with the `train` value it applies
// (undefined clears the filter and shows both).
const SPLIT_OPTIONS = [
  { value: "all", train: undefined, label: t.SplitAll },
  { value: "train", train: true, label: t.SplitTrain },
  { value: "test", train: false, label: t.SplitTest },
] as const;

type SplitOption = (typeof SPLIT_OPTIONS)[number]["value"];

interface DatasetImageFilterButtonProps {
  filters: DatasetImageFilters;
  filterActive: boolean;
  updateFilters: (changes: Partial<DatasetImageFilters>) => void;
}

// The dataset image list's filter control: an icon button that opens a
// popover of filter options.
export default function DatasetImageFilterButton({
  filters,
  filterActive,
  updateFilters,
}: DatasetImageFilterButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Tooltip title={t.Filter} arrow>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={t.Filter}
          data-testid="datasetImageList.filterButton"
          sx={{ color: filterActive ? "primary.main" : "text.secondary" }}
        >
          <SearchIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        data-testid="datasetImageList.filterPopover"
      >
        <Stack sx={{ p: 2, gap: 1, minWidth: 220 }}>
          <Typography variant="overline" sx={{ color: "text.secondary" }}>
            {t.SplitFilterLabel}
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={
              SPLIT_OPTIONS.find((o) => o.train === filters.train)?.value
            }
            onChange={(_, value: SplitOption | null) => {
              const option = SPLIT_OPTIONS.find((o) => o.value === value);
              if (option) updateFilters({ train: option.train });
            }}
          >
            {SPLIT_OPTIONS.map(({ value, label }) => (
              <ToggleButton
                key={value}
                value={value}
                data-testid={`datasetImageList.splitFilter.${value}`}
              >
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Popover>
    </>
  );
}
